import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { apiClient } from '../../../utils/apiClient';
import type {
  JurisprudenceAlertItem,
  AlertStatus,
  AlertBucket,
} from '../hooks/useJurisprudenceAlerts';

interface Props {
  alert: JurisprudenceAlertItem;
  onClose: () => void;
}

interface JurisRef {
  title: string;
  jurisdiction: string;
  decisionDate: string;
}

interface AlertDetail extends JurisprudenceAlertItem {
  suivi?: { id: string; jurisprudenceId: string; alertFrequency: string | null } | null;
}

const STATUS_LABELS: Record<AlertStatus, string> = {
  sent: 'Envoyée',
  pending: 'En attente',
  failed: 'Échec',
  suppressed: 'Supprimée',
};

const STATUS_BADGE: Record<AlertStatus, string> = {
  sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200',
  suppressed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

const BUCKET_LABELS: Record<AlertBucket, string> = {
  immediate: 'Immédiat',
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function scoreColor(percent: number): string {
  if (percent >= 80) return '#10b981';
  if (percent >= 60) return '#3b82f6';
  if (percent >= 40) return '#f59e0b';
  return '#ef4444';
}

const ScoreCircle: React.FC<{ percent: number; size?: number }> = ({ percent, size = 120 }) => {
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const color = scoreColor(percent);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
          className="text-gray-200 dark:text-mcdm-third-300"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
          {percent}%
        </span>
        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-mcdm-primary-200">
          Similarité
        </span>
      </div>
    </div>
  );
};

const sectionTitle =
  'text-xs uppercase tracking-wider text-mcdm-third-500 dark:text-mcdm-primary-200 mb-2';
const value = 'text-sm text-mcdm-third-500 dark:text-mcdm-primary-100';

async function fetchJurisRef(jurisprudenceId: string): Promise<JurisRef | null> {
  try {
    const response = await apiClient(`/jurisprudences/${jurisprudenceId}`);
    if (!response.ok) return null;
    const data = await response.json();
    const raw = data?.data ?? data;
    if (!raw) return null;
    const firstTitle = Array.isArray(raw.titlesAndSummaries) ? raw.titlesAndSummaries[0]?.titles?.[0] : undefined;
    return {
      title: firstTitle || raw.title || `Affaire n° ${raw.number ?? jurisprudenceId}`,
      jurisdiction: [raw.jurisdiction, raw.chamber].filter(Boolean).join(' — '),
      decisionDate: raw.decision_date ?? '',
    };
  } catch {
    return null;
  }
}

const AlertDetailModal: React.FC<Props> = ({ alert, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [detail, setDetail] = useState<AlertDetail | null>(null);
  const [originJuris, setOriginJuris] = useState<JurisRef | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiClient(`/jurisprudence-alerts/${alert._id}`);
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (data) setDetail(data as AlertDetail);
          if (data?.suivi?.jurisprudenceId) {
            const ref = await fetchJurisRef(data.suivi.jurisprudenceId);
            if (!cancelled) setOriginJuris(ref);
          }
        }
      } catch {
        // silencieux : on continue avec les données minimales déjà en props
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [alert._id]);

  const effective: AlertDetail = detail ?? alert;
  const globalPercent = Math.round(effective.matchScore * 100);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
            Détail de l’alerte
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-mcdm-third-500 rounded-lg p-4 bg-gray-50 dark:bg-mcdm-third-500">
              <p className={sectionTitle}>Votre jurisprudence suivie</p>
              {originJuris ? (
                <>
                  <p className="text-base font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 leading-snug">
                    {originJuris.title}
                  </p>
                  {(originJuris.jurisdiction || originJuris.decisionDate) && (
                    <p className="text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 mt-1">
                      {[originJuris.jurisdiction, originJuris.decisionDate].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {detail?.suivi?.jurisprudenceId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/jurisprudences/${detail.suivi!.jurisprudenceId}`)}
                      className="mt-2 text-xs text-mcdm-third-300 dark:text-mcdm-primary-100 hover:underline"
                    >
                      Voir la décision suivie →
                    </button>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400">Chargement…</p>
              )}
            </div>

            <div className="border-2 border-mcdm-third-300/60 dark:border-mcdm-primary-100/60 rounded-lg p-4 bg-mcdm-third-300/10 dark:bg-mcdm-third-500">
              <p className={sectionTitle}>Nouvelle correspondance</p>
              <p className="text-base font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 leading-snug">
                {effective.juris?.title ?? `Affaire ${effective.jurisprudenceId}`}
              </p>
              {effective.juris && (effective.juris.jurisdiction || effective.juris.decisionDate) && (
                <p className="text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 mt-1">
                  {[effective.juris.jurisdiction, effective.juris.decisionDate].filter(Boolean).join(' · ')}
                </p>
              )}
              <button
                type="button"
                onClick={() => navigate(`/jurisprudences/${effective.jurisprudenceId}`)}
                className="mt-2 text-xs text-mcdm-third-300 dark:text-mcdm-primary-100 hover:underline"
              >
                Voir la correspondance →
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center bg-gray-50 dark:bg-mcdm-third-500 rounded-lg py-5 border border-gray-200 dark:border-mcdm-third-500">
            <ScoreCircle percent={globalPercent} />
            <p className="text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 mt-3 max-w-md text-center">
              Score global de similarité entre votre jurisprudence suivie et la nouvelle correspondance.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={sectionTitle}>Statut</p>
              <span
                className={`inline-block px-2 py-1 rounded text-xs font-medium ${STATUS_BADGE[effective.status]}`}
              >
                {STATUS_LABELS[effective.status]}
              </span>
            </div>
            <div>
              <p className={sectionTitle}>Fréquence</p>
              <p className={value}>{BUCKET_LABELS[effective.bucket]}</p>
            </div>
            <div>
              <p className={sectionTitle}>Créée le</p>
              <p className={value}>{formatDate(effective.createdAt)}</p>
            </div>
            <div>
              <p className={sectionTitle}>Envoyée le</p>
              <p className={value}>{formatDate(effective.dispatchedAt)}</p>
            </div>
          </div>

          {isAdmin && (
            <div className="border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20 rounded-lg p-4 space-y-4">
              <p className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-300 font-semibold">
                Vue administrateur
              </p>
              <div>
                <p className={sectionTitle}>Décomposition du score</p>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 mb-1">
                      <span>Taxonomie (sous-domaines + problématiques)</span>
                      <span>{Math.round(effective.matchBreakdown.taxonomy * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-mcdm-third-500 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full"
                        style={{ width: `${Math.round(effective.matchBreakdown.taxonomy * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-mcdm-third-500 dark:text-mcdm-primary-200 mb-1">
                      <span>Similarité vectorielle (cosine)</span>
                      <span>{Math.round(effective.matchBreakdown.cosine * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-mcdm-third-500 rounded-full h-2">
                      <div
                        className="bg-indigo-500 h-2 rounded-full"
                        style={{ width: `${Math.round(effective.matchBreakdown.cosine * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {effective.emailMessageId && (
                <div>
                  <p className={sectionTitle}>Identifiant message SMTP</p>
                  <p className="text-xs font-mono text-mcdm-third-500 dark:text-mcdm-primary-200 break-all">
                    {effective.emailMessageId}
                  </p>
                </div>
              )}
            </div>
          )}

          {effective.smtpError && (
            <div>
              <p className={sectionTitle}>Erreur d'envoi</p>
              <pre className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
                {effective.smtpError}
              </pre>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-500"
          >
            Fermer
          </button>
          <button
            type="button"
            onClick={() => navigate(`/jurisprudences/${effective.jurisprudenceId}`)}
            className="px-4 py-2 text-sm rounded-md bg-mcdm-third-300 text-white hover:bg-mcdm-third-400"
          >
            Voir la nouvelle correspondance
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;
